/*
  Größenfinder (Version 2, Entwurf 29.10.2026, nicht live), gerendert von sections/groessenfinder.liquid.
  Lädt je Quell-Kollektion alle Seiten von sections/groessenfinder-daten (Section Rendering API), bündelt die Farben
  eines Modells (gleicher Modellname + gleiche Messwerte), rechnet je Modell mit derselben Regel wie der Größenrechner
  der Produktseite und zeigt nur Modelle, deren passende Größe in mindestens einer Farbe lieferbar ist.
*/
import { spannen, lesen, empfehlen } from './groessenrechner.js';

const SPEICHER = 'sd-fusslaenge'; /* derselbe Schlüssel wie der Größenrechner: Fußlänge je Gruppe kinder/erwachsene */
const SPEICHER_GRUPPE = 'sd-gf-gruppe';
const SPEICHER_ANSICHT = 'sd-gf-ansicht';
const CACHE = 'sd-gf-daten-v2';
const CACHE_MIN = 30;
const JE_SEITE = 24;
const FARBEN_SICHTBAR = 6;
const TYPEN = ['Sneakers', 'Halbschuhe', 'Boots', 'Booties', 'Stiefel', 'Stiefeletten', 'Gummistiefel', 'Sandalen', 'Sandaletten', 'Ballerinas', 'Hausschuhe'];
const GRUPPEN = { kinder: 'Kinder', damen: 'Damen', herren: 'Herren' };

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const preis = (cent) => euro.format(cent / 100);
const cm = (mm) => (mm / 10).toFixed(1).replace('.', ',');
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const nurZahl = (s) => parseFloat(String(s).replace(',', '.')) || 0;

function lesenSpeicher(schluessel, art = 'localStorage') {
  try {
    return JSON.parse(window[art].getItem(schluessel) || 'null');
  } catch (e) {
    return null;
  }
}

function schreibenSpeicher(schluessel, wert, art = 'localStorage') {
  try {
    window[art].setItem(schluessel, JSON.stringify(wert));
  } catch (e) {
    /* ohne Speicher geht der Finder trotzdem */
  }
}

/* Modellname und Farbe aus dem Titel: Modell endet beim letzten Wort in Großbuchstaben (KOEL: „… RANA TREK Dark Grey“),
   Versionsnummern direkt dahinter („TEX 2.0“) gehören noch zum Modell */
export function modellName(titel) {
  const w = String(titel).trim().split(/\s+/);
  let letzt = -1;
  w.forEach((x, i) => {
    if (i > 0 && /^[A-ZÄÖÜ0-9][A-ZÄÖÜ0-9.\-/&]+$/.test(x) && /[A-ZÄÖÜ]/.test(x)) letzt = i;
    else if (letzt === i - 1 && letzt > 0 && /^\d+(\.\d+)?$/.test(x)) letzt = i;
  });
  if (letzt < 0 || letzt === w.length - 1) return { name: titel, farbe: '' };
  return { name: w.slice(0, letzt + 1).join(' '), farbe: w.slice(letzt + 1).join(' ') };
}

/* Produkte (je Farbe eines) zu Modellen bündeln; Reihenfolge = erste Fundstelle (Kollektion: neueste zuerst) */
export function buendeln(produkte) {
  const modelle = new Map();
  produkte.forEach((p, index) => {
    const { name, farbe } = modellName(p.t);
    const sig = p.v.map((x) => `${x[1]}:${x[2]}`).join(',');
    const key = `${p.m}|${name}|${p.k}|${sig}`;
    if (!modelle.has(key)) {
      modelle.set(key, {
        key, marke: p.m, kinder: p.k, index, tags: new Set(),
        /* Marke steht über dem Namen, im Namen nicht wiederholen */
        name: name.toLowerCase().startsWith(`${p.m.toLowerCase()} `) ? name.slice(p.m.length + 1) : name,
        reihe: spannen(p.v.map(([v, s, i]) => ({ v, s, i, a: true })), p.k ? 12 : 8),
        farben: [],
      });
    }
    const m = modelle.get(key);
    p.g.forEach((t) => m.tags.add(t));
    m.farben.push({ farbe: farbe || p.t, url: p.u, bild: p.b, groessen: new Map(p.v.map(([v, s, , a, pr, st]) => [s, { v, a, preis: pr, streich: st }])) });
  });
  return [...modelle.values()];
}

/* Modell passt zur Gruppe: Tag Kinder/Damen/Herren; Erwachsenen-Modelle ohne Damen/Herren-Tag gelten für beide */
export function inGruppe(m, gruppe) {
  if (gruppe === 'kinder') return m.kinder || m.tags.has('Kinder');
  if (m.kinder || m.tags.has('Kinder')) return false;
  const d = m.tags.has('Damen');
  const h = m.tags.has('Herren');
  if (!d && !h) return true;
  return gruppe === 'damen' ? d : h;
}

/* -> null oder { groesse, auch, mehrPlatz, farben[] (lieferbar in der gezeigten Größe), passform } */
export function auswerten(m, fuss) {
  const e = empfehlen(m.reihe, fuss);
  if (e.art !== 'treffer') return null;
  const lieferbar = (g) => m.farben.filter((f) => f.groessen.get(g.s)?.a).map((f) => ({ ...f, g: f.groessen.get(g.s) }));
  let groesse = e.groesse;
  let farben = lieferbar(groesse);
  let mehrPlatz = false;
  if (!farben.length && e.auch) {
    /* genau passende Größe ausverkauft, Fuß knapp an der Grenze: die nächste zeigen */
    groesse = e.auch;
    farben = lieferbar(groesse);
    mehrPlatz = true;
  }
  if (!farben.length) return null;
  const auch = !mehrPlatz && e.auch && lieferbar(e.auch).length ? e.auch : null;
  /* Passform: Fuß möglichst mittig in der Spanne der Größe */
  const passform = Math.abs(fuss - (groesse.von + groesse.bis) / 2);
  return { modell: m, groesse, genau: e.groesse, auch, mehrPlatz, farben, passform, preisAb: Math.min(...farben.map((f) => f.g.preis)) };
}

async function seiteLaden(handle, seite) {
  const r = await fetch(`/collections/${encodeURIComponent(handle)}?section_id=groessenfinder-daten&page=${seite}`);
  if (!r.ok) throw new Error(`${handle} Seite ${seite}: ${r.status}`);
  const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
  return {
    info: JSON.parse(doc.querySelector('[data-gf-seiten]')?.textContent || '{"seiten":1}'),
    produkte: JSON.parse(doc.querySelector('[data-gf-produkte]')?.textContent || '[]'),
  };
}

async function allesLaden(quellen) {
  const schluessel = `${CACHE}:${quellen.join(',')}`;
  const alt = lesenSpeicher(schluessel, 'sessionStorage');
  if (alt && Date.now() - alt.zeit < CACHE_MIN * 60000) return alt.produkte;
  const gesehen = new Map();
  const ergebnisse = await Promise.all(
    quellen.map(async (h) => {
      const erste = await seiteLaden(h, 1);
      const rest = await Promise.all(Array.from({ length: Math.max(0, erste.info.seiten - 1) }, (_, k) => seiteLaden(h, k + 2)));
      return [erste, ...rest];
    })
  );
  for (const seiten of ergebnisse) for (const s of seiten) for (const p of s.produkte) if (!gesehen.has(p.id)) gesehen.set(p.id, p);
  const produkte = [...gesehen.values()];
  schreibenSpeicher(schluessel, { zeit: Date.now(), produkte }, 'sessionStorage');
  return produkte;
}

const Basis = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class GroessenFinder extends Basis {
  connectedCallback() {
    if (this.bereit) return;
    this.bereit = true;
    this.$ = (s) => this.querySelector(s);
    this.feld = this.$('.gf__fuss');
    this.meldung = this.$('.gf__meldung');
    this.start = this.$('.gf__start');
    this.ergebnis = this.$('.gf__ergebnis');
    this.status = this.$('.gf__status');
    this.liste = this.$('.gf__liste');
    this.filterBox = this.$('.gf__filter-box');
    this.filter = this.$('.gf__filter');
    this.mehr = this.$('.gf__mehr');
    this.typen = new Set();
    this.marken = new Set();
    this.ansicht = lesenSpeicher(SPEICHER_ANSICHT) === 'tabelle' ? 'tabelle' : 'kacheln';
    this.ansichtSetzen(this.ansicht);

    this.modelle = allesLaden(this.dataset.quellen.split(',').filter(Boolean))
      .then(buendeln)
      .catch((e) => {
        console.error('Größenfinder', e);
        return null;
      });

    this.querySelectorAll('input[name="gf-gruppe"]').forEach((r) =>
      r.addEventListener('change', () => {
        schreibenSpeicher(SPEICHER_GRUPPE, r.value);
        this.platzhalter();
        if (!this.feld.value.trim()) this.vorbelegen();
        else this.suchen(false);
      })
    );
    this.$('.gf__los').addEventListener('click', () => this.suchen(true));
    this.feld.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      this.suchen(true);
    });
    this.feld.addEventListener('input', () => {
      clearTimeout(this.tippen);
      this.tippen = setTimeout(() => {
        if (lesen(this.feld.value) !== null && this.gruppe) this.suchen(false);
      }, 500);
    });
    this.querySelectorAll('[data-gf-stufe]').forEach((b) =>
      b.addEventListener('click', () => {
        const mm = lesen(this.feld.value) ?? (this.gruppe === 'kinder' ? 150 : 240);
        this.feld.value = cm(Math.min(350, Math.max(80, mm + Number(b.dataset.gfStufe))));
        clearTimeout(this.tippen);
        this.tippen = setTimeout(() => this.suchen(false), 300);
      })
    );
    this.filter.addEventListener('click', (e) => {
      const b = e.target.closest('[data-gf-typ], [data-gf-marke]');
      if (!b) return;
      const menge = b.dataset.gfTyp ? this.typen : this.marken;
      const wert = b.dataset.gfTyp || b.dataset.gfMarke;
      menge.has(wert) ? menge.delete(wert) : menge.add(wert);
      this.zeigen(true);
    });
    this.$('.gf__zuruecksetzen').addEventListener('click', () => {
      this.typen.clear();
      this.marken.clear();
      this.zeigen(true);
    });
    this.$('.gf__sort').addEventListener('change', () => this.zeigen(true));
    this.querySelectorAll('[data-gf-ansicht]').forEach((b) =>
      b.addEventListener('click', () => {
        this.ansichtSetzen(b.dataset.gfAnsicht);
        schreibenSpeicher(SPEICHER_ANSICHT, this.ansicht);
        this.zeigen(false);
      })
    );
    const dialog = this.$('.gf__dialog');
    dialog?.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('.gf__dialog-zu')) dialog.close();
    });
    this.mehr.addEventListener('click', () => {
      this.anzahl += JE_SEITE;
      this.zeigen(false);
    });
    /* Farbe in einer Kachel wählen: Bild, Link und Preis wechseln */
    this.liste.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-gf-tabelle]');
      if (tab) {
        const t = this.sichtbar?.find((x) => x.modell.key === tab.dataset.gfTabelle);
        if (t) this.tabelleOeffnen(t, tab);
        return;
      }
      const b = e.target.closest('[data-gf-farbe]');
      if (!b) return;
      const karte = b.closest('[data-gf-modell]');
      const t = this.sichtbar?.find((x) => x.modell.key === karte.dataset.gfModell);
      const f = t?.farben[Number(b.dataset.gfFarbe)];
      if (!f) return;
      const url = `${f.url}?variant=${f.g.v}`;
      karte.querySelectorAll('[data-gf-link]').forEach((a) => (a.href = url));
      const img = karte.querySelector('.gf-k__bild img');
      if (img && f.bild) img.src = f.bild;
      karte.querySelector('.gf-k__farbe').textContent = f.farbe;
      karte.querySelector('.gf-k__preis').innerHTML = this.preisHtml(f.g);
      karte.querySelectorAll('[data-gf-farbe]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    });

    /* Einstieg: Werte aus der Adresse (teilbarer Link), sonst gemerkte Gruppe und Fußlänge */
    const q = new URLSearchParams(location.search);
    const gruppe = GRUPPEN[q.get('gruppe')] ? q.get('gruppe') : lesenSpeicher(SPEICHER_GRUPPE);
    const r = gruppe && this.$(`input[name="gf-gruppe"][value="${gruppe}"]`);
    if (r) {
      r.checked = true;
      this.platzhalter();
      const ausAdresse = lesen(q.get('fuss') || '');
      if (ausAdresse) {
        this.feld.value = cm(ausAdresse);
        this.suchen(false);
      } else this.vorbelegen();
    }
  }

  get gruppe() {
    return this.$('input[name="gf-gruppe"]:checked')?.value || null;
  }

  platzhalter() {
    this.feld.placeholder = this.gruppe === 'kinder' ? 'z. B. 17,5' : 'z. B. 24,5';
  }

  ansichtSetzen(a) {
    this.ansicht = a;
    this.querySelectorAll('[data-gf-ansicht]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gfAnsicht === a)));
  }

  /* eine früher eingegebene Fußlänge (auch vom Größenrechner der Produktseite) vorbelegen */
  vorbelegen() {
    const g = this.gruppe;
    if (!g) return;
    const mm = (lesenSpeicher(SPEICHER) || {})[g === 'kinder' ? 'kinder' : 'erwachsene'];
    if (!mm) return;
    this.feld.value = cm(mm);
    this.suchen(false);
  }

  async suchen(neu) {
    const fuss = lesen(this.feld.value);
    const gruppe = this.gruppe;
    this.meldung.textContent = '';
    if (!gruppe || fuss === null) {
      if (neu) this.meldung.textContent = !gruppe ? 'Bitte zuerst Kinder, Damen oder Herren wählen.' : 'Bitte die Fußlänge in cm eingeben, z. B. 24,5.';
      return;
    }
    const d = lesenSpeicher(SPEICHER) || {};
    d[gruppe === 'kinder' ? 'kinder' : 'erwachsene'] = fuss;
    schreibenSpeicher(SPEICHER, d);
    try {
      const u = new URL(location.href);
      u.searchParams.set('gruppe', gruppe);
      u.searchParams.set('fuss', cm(fuss));
      history.replaceState(null, '', u);
    } catch (e) {
      /* Adresse bleibt, wie sie ist */
    }

    this.start.hidden = true;
    this.ergebnis.hidden = false;
    this.status.textContent = 'Passende Modelle werden gesucht …';
    this.liste.innerHTML = `<ul class="gf__lade" aria-hidden="true">${'<li></li>'.repeat(6)}</ul>`;
    this.mehr.hidden = true;
    if (neu && window.matchMedia('(max-width: 989px)').matches) this.ergebnis.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const modelle = await this.modelle;
    if (!modelle) {
      this.status.textContent = '';
      this.liste.innerHTML = '<div class="gf__leer"><p><strong>Die Modelle konnten gerade nicht geladen werden.</strong></p><p>Bitte lade die Seite neu.</p></div>';
      return;
    }
    this.fuss = fuss;
    this.treffer = modelle.filter((m) => inGruppe(m, gruppe)).map((m) => auswerten(m, fuss)).filter(Boolean);
    this.typen.clear();
    this.marken.clear();
    this.zeigen(true);
  }

  gefiltert() {
    const liste = this.treffer.filter(
      ({ modell: m }) => (!this.typen.size || [...this.typen].some((t) => m.tags.has(t))) && (!this.marken.size || this.marken.has(m.marke))
    );
    const sort = this.$('.gf__sort').value;
    const fn = {
      passform: (a, b) => a.passform - b.passform || a.modell.index - b.modell.index,
      neu: (a, b) => a.modell.index - b.modell.index,
      'preis-auf': (a, b) => a.preisAb - b.preisAb,
      'preis-ab': (a, b) => b.preisAb - a.preisAb,
    }[sort];
    return liste.sort(fn);
  }

  filterZeigen() {
    const typen = TYPEN.map((t) => [t, this.treffer.filter(({ modell }) => modell.tags.has(t)).length]).filter(([, n]) => n);
    const marken = [...new Set(this.treffer.map(({ modell }) => modell.marke))].sort();
    let h = '';
    if (typen.length > 1)
      h += '<span class="gf__filter-titel">Schuhtyp</span>' + typen.map(([t, n]) => `<button type="button" class="gf__chip" data-gf-typ="${esc(t)}" aria-pressed="${this.typen.has(t)}">${esc(t)}<span>${n}</span></button>`).join('');
    if (marken.length > 1)
      h += '<span class="gf__filter-titel">Marke</span>' + marken.map((m) => `<button type="button" class="gf__chip" data-gf-marke="${esc(m)}" aria-pressed="${this.marken.has(m)}">${esc(m)}</button>`).join('');
    this.filter.innerHTML = h;
    this.filterBox.hidden = !h;
    this.$('.gf__zuruecksetzen').hidden = !this.typen.size && !this.marken.size;
  }

  zeigen(vonVorn) {
    if (!this.treffer) return;
    if (vonVorn) this.anzahl = JE_SEITE;
    this.filterZeigen();
    const liste = this.gefiltert();
    const g = GRUPPEN[this.gruppe];
    if (!liste.length) {
      this.status.innerHTML = '';
      this.liste.innerHTML = `<div class="gf__leer"><p><strong>Für ${cm(this.fuss)} cm Fußlänge (${g}) haben wir gerade kein gemessenes Modell in deiner Größe auf Lager.</strong></p><p>Prüfe die Eingabe oder setze die Filter zurück. Gern beraten wir dich auch persönlich.</p></div>`;
      this.mehr.hidden = true;
      return;
    }
    this.status.innerHTML = `<strong>${liste.length} ${liste.length === 1 ? 'Modell passt' : 'Modelle passen'}</strong> bei ${cm(this.fuss)} cm Fußlänge (${g})`;
    /* häufigste Größen als Orientierung */
    const zaehl = new Map();
    liste.forEach((t) => zaehl.set(t.groesse.s, (zaehl.get(t.groesse.s) || 0) + 1));
    const groessen = [...zaehl].sort((a, b) => nurZahl(a[0]) - nurZahl(b[0]));
    const info = `<div class="gf__groessen-info" aria-label="Deine Größen">${groessen.map(([s, n]) => `<span>Größe ${esc(s)}: ${n} ${n === 1 ? 'Modell' : 'Modelle'}</span>`).join('')}</div>`;
    this.sichtbar = liste.slice(0, this.anzahl);
    this.liste.innerHTML = info + (this.ansicht === 'tabelle' ? this.tabelle(this.sichtbar) : `<ul class="gf__kacheln" role="list">${this.sichtbar.map((t) => this.kachel(t)).join('')}</ul>`);
    this.mehr.hidden = liste.length <= this.anzahl;
    this.mehr.textContent = `Weitere Modelle anzeigen (${liste.length - Math.min(this.anzahl, liste.length)})`;
  }

  preisHtml(g) {
    return `${preis(g.preis)}${g.streich ? `<s>${preis(g.streich)}</s>` : ''}`;
  }

  hinweis(t) {
    if (t.mehrPlatz) return `<p class="gf-k__hinweis">Größe ${esc(t.genau.s)} ist ausverkauft, ${esc(t.groesse.s)} gibt dir etwas mehr Platz.</p>`;
    if (t.auch) return `<p class="gf-k__hinweis">Knapp an der Grenze. Mit mehr Platz: ${esc(t.auch.s)}</p>`;
    return '';
  }

  /* Größentabelle als Fenster über der Seite (in der schmalen Kachel wäre sie abgeschnitten) */
  tabelleOeffnen(t, ausloeser) {
    const d = this.$('.gf__dialog');
    if (!d || typeof d.showModal !== 'function') return;
    this.$('.gf__dialog-titel').textContent = `Größentabelle ${t.modell.name}`;
    this.$('.gf__dialog-inhalt').innerHTML =
      `<p class="gf__dialog-info">Deine Fußlänge: <strong>${cm(this.fuss)} cm</strong>, deine Größe: <strong>${esc(t.groesse.s)}</strong>. Spielraum ${t.modell.kinder ? 12 : 8} mm ist in der Fußlänge schon eingerechnet.</p>` +
      this.groessenTabelle(t);
    d.showModal();
    d.addEventListener('close', () => ausloeser.focus(), { once: true });
  }

  /* Größentabelle eines Modells: alle Größen, deine markiert, lieferbar in wie vielen Farben */
  groessenTabelle(t) {
    const m = t.modell;
    const zeilen = m.reihe
      .map((x) => {
        const n = m.farben.filter((f) => f.groessen.get(x.s)?.a).length;
        const cls = x.s === t.groesse.s ? 'ist-treffer' : n ? '' : 'ist-aus';
        return `<tr class="${cls}"><th scope="row">${esc(x.s)}</th><td class="zahl">${cm(x.i)}</td><td class="zahl">${cm(x.von)} bis ${cm(x.bis)}</td><td>${n ? (m.farben.length > 1 ? `${n} ${n === 1 ? 'Farbe' : 'Farben'}` : 'ja') : 'nein'}</td></tr>`;
      })
      .join('');
    return `<div class="gf__tabelle-rahmen"><table class="gf__tab"><thead><tr><th scope="col">Größe</th><th scope="col" class="zahl">Innen&shy;länge (cm)</th><th scope="col" class="zahl">Passt für Fuß&shy;länge (cm)</th><th scope="col">Liefer&shy;bar</th></tr></thead><tbody>${zeilen}</tbody></table></div>`;
  }

  farbenHtml(t) {
    if (t.farben.length < 2) return '';
    const sichtbar = t.farben.slice(0, FARBEN_SICHTBAR);
    const rest = t.farben.length - sichtbar.length;
    return `<div class="gf-farben" aria-label="Farben in deiner Größe">${sichtbar
      .map((f, i) => `<button type="button" data-gf-farbe="${i}" aria-pressed="${i === 0}" aria-label="${esc(f.farbe)}" title="${esc(f.farbe)}">${f.bild ? `<img src="${esc(f.bild.replace(/width=\d+/, 'width=80'))}" alt="" width="30" height="30" loading="lazy">` : ''}</button>`)
      .join('')}${rest > 0 ? `<span class="gf-farben__mehr">+${rest}</span>` : ''}</div>`;
  }

  kachel(t) {
    const m = t.modell;
    const f = t.farben[0];
    const url = `${f.url}?variant=${f.g.v}`;
    const bild = f.bild ? `<img src="${esc(f.bild)}" alt="${esc(m.name)}" width="480" height="480" loading="lazy">` : '';
    return `<li class="gf-k" data-gf-modell="${esc(m.key)}">
      <a class="gf-k__bild" href="${esc(url)}" data-gf-link tabindex="-1" aria-hidden="true">${bild}</a>
      <p class="gf-k__marke">${esc(m.marke)}</p>
      <h3 class="gf-k__name"><a href="${esc(url)}" data-gf-link>${esc(m.name)}</a> <span class="gf-k__farbe">${esc(f.farbe === m.name ? '' : f.farbe)}</span></h3>
      <div class="gf-k__groesse"><span class="gf-badge">Größe ${esc(t.groesse.s)}</span><span class="gf-k__passt">passt für ${cm(t.groesse.von)} bis ${cm(t.groesse.bis)} cm</span></div>
      ${this.hinweis(t)}
      ${this.farbenHtml(t)}
      <p class="gf-k__preis">${this.preisHtml(f.g)}</p>
      <button type="button" class="gf-k__tab-knopf" data-gf-tabelle="${esc(m.key)}">Größentabelle ansehen</button>
    </li>`;
  }

  tabelle(liste) {
    const zeilen = liste
      .map((t) => {
        const m = t.modell;
        const f = t.farben[0];
        const url = `${f.url}?variant=${f.g.v}`;
        const hinweis = t.auch ? `knapp, mit mehr Platz: ${esc(t.auch.s)}` : t.mehrPlatz ? `${esc(t.genau.s)} ausverkauft` : '';
        return `<tr>
          <td><div class="gf-zeile__modell">${f.bild ? `<img src="${esc(f.bild.replace(/width=\d+/, 'width=160'))}" alt="" width="56" height="56" loading="lazy">` : ''}<div><small>${esc(m.marke)}</small><br><a href="${esc(url)}">${esc(m.name)}</a><br><small>${t.farben.length > 1 ? `${t.farben.length} Farben in deiner Größe` : esc(f.farbe)}</small> <button type="button" class="gf-k__tab-knopf" data-gf-tabelle="${esc(m.key)}">Größentabelle</button></div></div></td>
          <td><span class="gf-badge">${esc(t.groesse.s)}</span>${hinweis ? `<br><small>${hinweis}</small>` : ''}</td>
          <td class="zahl">${cm(t.groesse.von)} bis ${cm(t.groesse.bis)}<br><small>${t.groesse.i - this.fuss} mm Platz</small></td>
          <td class="zahl">${t.farben.length > 1 ? 'ab ' : ''}${preis(t.preisAb)}</td>
        </tr>`;
      })
      .join('');
    return `<div class="gf__tabelle-rahmen"><table class="gf__tab gf__tab--ergebnis">
      <thead><tr><th scope="col">Modell</th><th scope="col">Deine Größe</th><th scope="col" class="zahl">Fußlänge (cm)</th><th scope="col" class="zahl">Preis</th></tr></thead>
      <tbody>${zeilen}</tbody></table></div>`;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('groessen-finder')) {
  customElements.define('groessen-finder', GroessenFinder);
}
